import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ALL_EXPORT_COLUMNS,
  LEAD_EXPORT_COLUMNS,
  LeadExportColumn,
  REQUIRED_EXPORT_COLUMN,
  normalizeExportColumns,
} from '../../../../utils/lead-export-columns';

/**
 * Pick the columns of an export file. Name is always included (checked and
 * disabled), so at least one column is always exported.
 */
@Component({
  selector: 'app-export-columns-picker',
  standalone: true,
  imports: [NgFor, NgIf, NgTemplateOutlet],
  host: { '(document:mousedown)': 'onDocumentDown($event)', '(document:keydown.escape)': 'menuOpen = false' },
  template: `
    <ng-template #checklist>
      <div class="flex gap-3 text-xs mb-2">
        <button type="button" class="font-medium text-violet-600 hover:text-violet-700 disabled:text-gray-300"
                [disabled]="disabled || allSelected" (click)="selectAll()">Select all</button>
        <button type="button" class="font-medium text-violet-600 hover:text-violet-700 disabled:text-gray-300"
                [disabled]="disabled || value.length === 1" (click)="onlyName()">Only Name</button>
      </div>
      <div class="grid grid-cols-2 gap-x-3 gap-y-1.5">
        <label *ngFor="let col of columns" class="flex items-center gap-2 text-sm"
               [class.text-gray-500]="col.key === required" [class.text-gray-700]="col.key !== required"
               [class.cursor-pointer]="col.key !== required"
               [attr.title]="col.key === required ? 'Name is always included' : null">
          <input type="checkbox" class="rounded border-gray-300 text-violet-600 focus:ring-violet-500 disabled:opacity-60"
                 [checked]="isSelected(col.key)" [disabled]="disabled || col.key === required"
                 (change)="toggle(col.key)">
          {{ col.label }}
          <span *ngIf="col.key === required" class="text-xs text-gray-400">(always)</span>
        </label>
      </div>
    </ng-template>

    <!-- Section inside an export dialog -->
    <div *ngIf="variant === 'section'" class="rounded-lg border border-gray-200 mb-4">
      <button type="button" class="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm"
              [attr.aria-expanded]="open" (click)="open = !open">
        <span class="font-medium text-gray-900">
          Columns
          <span class="font-normal text-gray-500 ml-1">{{ countLabel }}</span>
        </span>
        <span class="text-gray-400 text-xs transition-transform" [class.rotate-180]="open">&#9660;</span>
      </button>
      <div *ngIf="open" class="border-t border-gray-100 px-3 pb-3 pt-2">
        <ng-container *ngTemplateOutlet="checklist"></ng-container>
      </div>
    </div>

    <!-- Dropdown next to an export button -->
    <div *ngIf="variant === 'menu'" class="relative inline-block">
      <button type="button"
              class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              [disabled]="disabled" [attr.aria-expanded]="menuOpen" aria-haspopup="dialog" (click)="menuOpen = !menuOpen">
        Columns <span class="font-normal text-gray-500">{{ countLabel }}</span>
        <span class="text-gray-400 text-xs transition-transform" [class.rotate-180]="menuOpen">&#9660;</span>
      </button>
      <div *ngIf="menuOpen" role="dialog" aria-label="Export columns"
           class="absolute z-30 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
           [class.right-0]="align === 'right'" [class.left-0]="align === 'left'">
        <p class="text-xs text-gray-500 mb-2">Columns in the Excel file. Name is always included.</p>
        <ng-container *ngTemplateOutlet="checklist"></ng-container>
      </div>
    </div>
  `,
})
export class ExportColumnsPickerComponent {
  @Input({ required: true }) value: LeadExportColumn[] = [];
  @Input() disabled = false;
  @Input() open = false;
  /** 'section' = collapsible block in a dialog, 'menu' = dropdown next to a button. */
  @Input() variant: 'section' | 'menu' = 'section';
  @Input() align: 'left' | 'right' = 'left';
  @Output() valueChange = new EventEmitter<LeadExportColumn[]>();

  readonly columns = LEAD_EXPORT_COLUMNS;
  readonly required = REQUIRED_EXPORT_COLUMN;
  readonly total = ALL_EXPORT_COLUMNS.length;

  menuOpen = false;

  constructor(private host: ElementRef<HTMLElement>) {}

  get countLabel(): string {
    return this.allSelected ? 'All' : `${this.value.length} of ${this.total}`;
  }

  onDocumentDown(event: MouseEvent): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }

  get allSelected(): boolean {
    return this.value.length === this.total;
  }

  isSelected(key: LeadExportColumn): boolean {
    return this.value.includes(key);
  }

  toggle(key: LeadExportColumn): void {
    if (key === this.required) {
      return;
    }
    const next = this.isSelected(key) ? this.value.filter(k => k !== key) : [...this.value, key];
    this.valueChange.emit(normalizeExportColumns(next));
  }

  selectAll(): void {
    this.valueChange.emit([...ALL_EXPORT_COLUMNS]);
  }

  onlyName(): void {
    this.valueChange.emit([this.required]);
  }
}
